import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertOctagon, Download, RefreshCcw, ChevronRight, Zap } from "lucide-react";
import api, { formatDetail } from "@/lib/api";
import { toast } from "sonner";
import { formatIDR, fmtDate } from "@/lib/constants";
import { useAccess } from "@/lib/access";

/**
 * Widget khusus BOD/FAT: menampilkan top-N pengajuan petty cash yang transferred tapi
 * realisasi belum lengkap, sort by hari terlama sejak transfer. Include tombol trigger
 * manual scan + download Excel audit log reminder.
 */
export default function BacklogRealisasiWidget({ user }) {
  const nav = useNavigate();
  const { canPerm } = useAccess();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  // Route via matrix `petty_cash.view` — admin dapat delegate widget backlog ke divisi lain.
  const isEligible = !!user && canPerm(user, "petty_cash", "view");

  const load = useCallback(async () => {
    if (!isEligible) return;
    setLoading(true);
    try {
      const { data } = await api.get("/petty-cash/backlog-actual", { params: { limit: 5 } });
      setRows(data || []);
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, [isEligible]);

  useEffect(() => { load(); }, [load]);

  const triggerScan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/petty-cash/scan-incomplete-actual");
      const parts = [];
      if (data.reminded > 0) parts.push(`${data.reminded} reminder terkirim`);
      if (data.escalated > 0) parts.push(`${data.escalated} escalate ke BOD`);
      toast.success(parts.join(" · ") || "Scan selesai — tidak ada backlog");
      await load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setScanning(false); }
  };

  const downloadLog = async () => {
    try {
      const res = await api.get("/petty-cash/reminder-log.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `petty_cash_reminder_log_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Reminder log terdownload");
    } catch (e) { toast.error("Gagal download log"); }
  };

  if (!isEligible) return null;

  return (
    <section className="bg-white border border-slate-200" data-testid="widget-backlog-realisasi">
      <header className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-rose-50">
        <div className="flex items-center gap-2">
          <AlertOctagon size={16} className="text-rose-700" />
          <h2 className="font-display text-lg tracking-tight text-[#0B1D3A]">Backlog Realisasi Petty Cash</h2>
          <span data-testid="backlog-count" className={`px-2 py-0.5 text-[10px] font-bold tabular-nums ${rows.length > 0 ? "bg-rose-600 text-white" : "bg-slate-200 text-slate-500"}`}>
            {rows.length}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="btn-backlog-scan" onClick={triggerScan} disabled={scanning}
            className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-600 hover:border-[#FF5A1F] hover:text-[#FF5A1F] inline-flex items-center gap-1 disabled:opacity-40">
            <RefreshCcw size={11} className={scanning ? "animate-spin" : ""} /> {scanning ? "Scan…" : "Scan Sekarang"}
          </button>
          <button data-testid="btn-backlog-download" onClick={downloadLog}
            className="px-2 py-1 text-[10px] uppercase tracking-wider font-semibold border border-slate-300 text-slate-600 hover:border-emerald-600 hover:text-emerald-700 inline-flex items-center gap-1">
            <Download size={11} /> Audit Log
          </button>
        </div>
      </header>
      <div className="divide-y divide-slate-100">
        {loading && <div className="px-4 py-6 text-center text-slate-400 text-xs italic">Memuat…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-4 py-6 text-center text-emerald-700 text-xs">
            ✓ Tidak ada backlog realisasi. Semua pengajuan aktual up-to-date.
          </div>
        )}
        {!loading && rows.map((r) => {
          const isEscalated = !!r.escalated_at;
          const isHot = r.days_since_transfer >= 14;
          const barClass = isEscalated ? "border-l-4 border-l-rose-600 bg-rose-50/50" : isHot ? "border-l-4 border-l-rose-500 bg-rose-50/30" : "border-l-4 border-l-amber-500";
          return (
            <button
              key={r.id}
              onClick={() => nav("/petty-cash")}
              data-testid={`backlog-row-${r.id}`}
              className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition-colors ${barClass}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-sm text-[#0B1D3A]">{r.user_name}</span>
                    <span className="text-[10px] px-1.5 py-0.5 border border-slate-300 bg-slate-50 text-slate-600 uppercase tracking-wider">{r.week_label}</span>
                    {isEscalated && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-rose-600 text-white font-bold uppercase tracking-wider inline-flex items-center gap-1">
                        <Zap size={9} /> Escalated
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 truncate">{r.project_name}</div>
                  <div className="text-[10px] mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold tabular-nums ${isHot ? "text-rose-700" : "text-amber-700"}`}>{r.days_since_transfer} hari sejak transfer</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-600 tabular-nums">{r.incomplete_items_count} / {r.total_items} item belum diisi</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-600">{r.reminder_count}× reminder</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-slate-700 font-semibold tabular-nums">{formatIDR(r.total)}</span>
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 shrink-0" />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
